/*
 * Copyright (c) 2023
 *
 * The source code and all documents related to the source code ("Material") are owned by
 * Frictionless Systems, LLC.
 *
 * The Material contains trade secrets, proprietary information, and confidential information.
 * No part of the Material may be used, copied, reproduced, modified, published, distributed, or
 * disclosed in any way without Frictionless Systems, LLC’s express prior written permission.
 *
 * The Material may be protected under patent, trademark, copyright, or trade secret laws.  No
 * license is granted to you by disclosure or delivery of the Materials.  Any license must be
 * express and approved by Frictionless Systems, LLC in writing.
 *
 * “Frictionless Systems” and “Impulse” are registered trademarks of Frictionless Systems, LLC.
 */

package systems.frictionless.impulse.browser.style

import org.jetbrains.compose.web.ExperimentalComposeWebApi
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.CSSStyleRuleBuilder
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.GenericStyleSheetBuilder
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.background
import org.jetbrains.compose.web.css.borderRadius
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.flexWrap
import org.jetbrains.compose.web.css.fontSize
import org.jetbrains.compose.web.css.fr
import org.jetbrains.compose.web.css.gridColumn
import org.jetbrains.compose.web.css.gridRow
import org.jetbrains.compose.web.css.gridTemplateColumns
import org.jetbrains.compose.web.css.gridTemplateRows
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.left
import org.jetbrains.compose.web.css.lineHeight
import org.jetbrains.compose.web.css.marginBottom
import org.jetbrains.compose.web.css.marginLeft
import org.jetbrains.compose.web.css.marginRight
import org.jetbrains.compose.web.css.marginTop
import org.jetbrains.compose.web.css.maxWidth
import org.jetbrains.compose.web.css.minus
import org.jetbrains.compose.web.css.overflowY
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.paddingBottom
import org.jetbrains.compose.web.css.paddingLeft
import org.jetbrains.compose.web.css.paddingRight
import org.jetbrains.compose.web.css.paddingTop
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.position
import org.jetbrains.compose.web.css.pt
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.times
import org.jetbrains.compose.web.css.top
import org.jetbrains.compose.web.css.transform
import org.jetbrains.compose.web.css.value
import org.jetbrains.compose.web.css.vh
import org.jetbrains.compose.web.css.vw
import org.jetbrains.compose.web.css.width
import systems.frictionless.impulse.browser.style.constants.AUTO
import systems.frictionless.impulse.browser.style.constants.BODY
import systems.frictionless.impulse.browser.style.constants.BUTTON
import systems.frictionless.impulse.browser.style.constants.FIT_CONTENT
import systems.frictionless.impulse.browser.style.constants.H1
import systems.frictionless.impulse.browser.style.constants.HIDDEN
import systems.frictionless.impulse.browser.style.constants.INHERIT
import systems.frictionless.impulse.browser.style.constants.INITIAL
import systems.frictionless.impulse.browser.style.constants.INPUT
import systems.frictionless.impulse.browser.style.constants.MS_OVERFLOW_STYLE
import systems.frictionless.impulse.browser.style.constants.NONE
import systems.frictionless.impulse.browser.style.constants.P
import systems.frictionless.impulse.browser.style.constants.TEXTAREA
import systems.frictionless.impulse.browser.style.constants.UNSET
import systems.frictionless.impulse.browser.style.constants.checkMarkHeight
import systems.frictionless.impulse.browser.style.constants.grid12
import systems.frictionless.impulse.browser.style.constants.grid16
import systems.frictionless.impulse.browser.style.constants.grid2
import systems.frictionless.impulse.browser.style.constants.grid20
import systems.frictionless.impulse.browser.style.constants.grid24
import systems.frictionless.impulse.browser.style.constants.grid3
import systems.frictionless.impulse.browser.style.constants.grid4
import systems.frictionless.impulse.browser.style.constants.grid6
import systems.frictionless.impulse.browser.style.constants.grid9
import systems.frictionless.impulse.browser.style.constants.navigationLogoPaddingLeft
import systems.frictionless.impulse.browser.style.constants.progressBarBottomPadding
import systems.frictionless.impulse.browser.style.constants.progressBarHorizontalPadding
import systems.frictionless.impulse.browser.style.constants.progressBarInnerPadding
import systems.frictionless.impulse.browser.style.constants.progressBarVerticalPadding
import systems.frictionless.impulse.browser.style.support.Margin
import systems.frictionless.impulse.browser.style.support.backgroundPositionX
import systems.frictionless.impulse.browser.style.support.backgroundPositionY
import systems.frictionless.impulse.browser.style.support.boxShadow
import systems.frictionless.impulse.browser.style.support.gridGap
import systems.frictionless.impulse.browser.style.support.marginBlockEnd
import systems.frictionless.impulse.browser.style.support.marginBlockStart
import systems.frictionless.impulse.browser.style.support.marginHorizontal
import systems.frictionless.impulse.browser.style.support.marginVertical
import systems.frictionless.impulse.browser.style.support.paddingBottom
import systems.frictionless.impulse.browser.style.support.placeholder
import systems.frictionless.impulse.browser.style.support.repeat
import systems.frictionless.impulse.browser.style.support.scrollbarWidth
import systems.frictionless.impulse.browser.style.support.webkitScrollbar
import systems.frictionless.impulse.browser.style.support.width

@OptIn(ExperimentalComposeWebApi::class)
@Suppress("MagicNumber", "LongMethod")
fun AppStyleSheet.desktopDesign(genericStyleSheetBuilder: GenericStyleSheetBuilder<CSSStyleRuleBuilder>) {
    genericStyleSheetBuilder.apply {
        root style {
            AppStyleSheetVariables.fontSizeNormal(14.pt)
            AppStyleSheetVariables.fontSizeSmall(12.pt)
            AppStyleSheetVariables.fontSizeBigger(16.pt)
            AppStyleSheetVariables.fontSizeHeader(28.pt)
            AppStyleSheetVariables.fontSizeTextAreaPlaceholder(12.pt)
            AppStyleSheetVariables.fontSizeQuestionText(22.pt)
            AppStyleSheetVariables.fontSizeQuestionHintText(14.pt)
            AppStyleSheetVariables.fontSizeTextAreaPlaceholder(14.pt)
        }
        type(BODY) style {
            overflowY(HIDDEN)
            background(
                "url($ASSETS_PATH/img/background_lb.svg) left bottom no-repeat, url($ASSETS_PATH/img/background_rb.svg) right bottom no-repeat, rgb(246,246,246)"
            )
        }
        type(P) style {
            lineHeight(140.percent)
            marginBlockStart(1.em)
            marginBlockEnd(1.em)
        }
        className(contentContainer) style {
            display(DisplayStyle.Grid)
            gridTemplateColumns(
                repeat(
                    cells = 10,
                    flexFactor = 1.fr
                )
            )
            gridTemplateRows(
                repeat(
                    cells = 10,
                    flexFactor = 1.fr
                )
            )
            gridGap(0.px)
            width(100.vw)
            height(100.vh)
            padding(0.px)
        }
        className(navigationContainer) style {
            gridColumn(1, 3)
            gridRow(1, 10)
            background("url($ASSETS_PATH/img/impulse-logo.svg) left top no-repeat")
            backgroundPositionX(navigationLogoPaddingLeft)
            backgroundPositionY(grid24)
            display(DisplayStyle.Block)
        }
        className(mainContainer) style {
            // Grid
            // Two missing columns to maintain symmetry
            gridColumn(3, 9)
            gridRow(1, 10)
            padding(grid6)
            maxWidth(UNSET)
            marginHorizontal(Margin.UNSET)
        }
        className(progressContainer) style {
            gridRow(10, 11)
            gridColumn(1, 11)
        }
        // No progress bar variant of main layout
        desc(
            combine(className(contentContainer), className(noProgress)),
            className(progressContainer)
        ) style {
            display(DisplayStyle.None)
        }
        desc(
            combine(className(contentContainer), className(noProgress)),
            className(mainContainer)
        ) style {
            gridRow(1, 11)
        }
        desc(
            combine(className(contentContainer), className(noProgress)),
            className(navigationContainer)
        ) style {
            gridRow(1, 11)
        }
        // No navigation variant of main layout
        desc(
            combine(className(contentContainer), className(noNavigation)),
            className(navigationContainer)
        ) style {
            display(DisplayStyle.None)
        }
        desc(
            combine(className(contentContainer), className(noNavigation)),
            className(mainContainer)
        ) style {
            gridColumn(1, 11)
        }
        className(progressBar) style {
            height(grid20)
            width(100.vw - 2 * progressBarHorizontalPadding)
            marginLeft(progressBarVerticalPadding)
            marginBottom(progressBarBottomPadding)
            boxShadow("0 4px 8px 0 rgb(0 0 0 / 20%), 0 6px 20px 0 rgb(0 0 0 / 19%)")
            borderRadius(grid2)
            paddingTop(progressBarInnerPadding)
            paddingBottom(progressBarInnerPadding)
        }
        desc(className(progressBar), type(BUTTON)) style {
            marginLeft(progressBarInnerPadding)
            marginRight(progressBarInnerPadding)
        }
        desc(className(progressBar), className(progressOuter)) style {
            marginLeft(progressBarInnerPadding)
        }
        desc(className(intro), className(list)) style {
            width(AppStyleSheetVariables.elementWidthMedium.value())
        }
        className(introItem) style {
            marginTop(grid4)
        }
        className(introItemNote) style {
            marginTop(grid4)
            marginBottom(grid4)
        }
        desc(className(login), type(INPUT)) style {
            maxWidth(AppStyleSheetVariables.elementWidthSmall.value())
        }
        combine(type(TEXTAREA), placeholder) style {
            fontSize(AppStyleSheetVariables.fontSizeTextAreaPlaceholder.value())
        }
        className(questionMultiChoiceBox) style {
            marginVertical(grid16)
            display(DisplayStyle.Flex)
            flexWrap(FlexWrap.Wrap)
        }
        className(questionText) style {
            marginTop(grid12)
            lineHeight(140.percent)
        }
        className(questionTextArea) style {
            marginTop(grid16)
            width(80.percent)
            maxWidth(INITIAL)
        }
        desc(className(questionTextArea), type(TEXTAREA)) style {
            width(INHERIT)
            height(grid24)
            maxWidth(INITIAL)
        }
        className(questionHint) style {
            marginTop(grid9)
            lineHeight(140.percent)
        }
        className(singleChoice) style {
            fontSize(AppStyleSheetVariables.fontSizeSmall.value())
            height(checkMarkHeight)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
        }

        desc(className(insights), type(H1)) style {
            marginBlockStart(INITIAL)
            marginBlockEnd(INITIAL)
        }

        desc(className(insights), className(tabs)) style {
            marginTop(grid12)
            marginBottom(grid6)
            paddingBottom(grid6)
            width(FIT_CONTENT)
        }

        desc(className(insights), className(insightTexts)) style {
            height(100.vh - 270.px)
            overflowY(AUTO)
            property(MS_OVERFLOW_STYLE, NONE)
            scrollbarWidth(NONE)
        }

        desc(insights, combine(className(insightTexts), webkitScrollbar)) style {
            display(DisplayStyle.None)
        }

        desc(
            desc(className(insights), className(insightTexts)),
            combine(className(insightsSection), lastChild)
        ) style {
            paddingBottom(UNSET)
        }

        desc(desc(className(insights), className(tabs)), className(tabName)) style {
            paddingLeft(grid6)
            paddingRight(grid6)
            paddingTop(grid3)
            paddingBottom(grid3)
            fontSize(AppStyleSheetVariables.fontSizeNormal.value())
        }

        combine(className(centeredOnScreen), className(ignoreCenteringOnMobile)) style {
            position(Position.Absolute)
            top(50.percent)
            left(50.percent)
            transform {
                translate(
                    (-50).percent,
                    (-50).percent
                )
            }
        }

        className(ignoreCenteringOnMobile) style {
            marginTop(0.px)
        }
    }
}
